import { resolveURLTemplates, track, progressFormatted } from './vast-util';
import EventEmitter from 'events';
import { MediaFile } from './media-file';
import { VastIconType } from './vast-icon';

interface EndScreen {
  clickTracking?: string[];
  clickThrough?: string[];
}

class VASTTracker extends EventEmitter {
  muted: boolean;
  impressed: boolean;
  skipable: boolean;
  skipDelay: number;
  macros: Record<string, any>;
  trackingEvents: Record<string, string[]>;
  mediaFiles: MediaFile[];
  emitAlwaysEvents: string[];
  vastIcon?: VastIconType;
  apiFramework?: string;
  creativeViewed: boolean;

  clickThroughURLTemplates: string[];
  clickTrackingURLTemplates: string[];
  impressionURLTemplates: string[];
  errorURLTemplates: string[];
  viewableImpressionURLTemplates: string[];

  duration: number;
  quartiles: Record<string, number>;
  progress: number;

  constructor(
    clickTrackingURLTemplates: string[],
    clickThroughURLTemplates: string[],
    impressionURLTemplates: string[],
    errorURLTemplates: string[],
    viewableImpressionURLTemplates: string[],
    trackingEvents: Record<string, string[]>,
    mediaFiles: MediaFile[],
    duration: number,
    skipDelay: number,
    muted: boolean,
    macros: Record<string, any>,
    vastIcon?: VastIconType,
    apiFramework?: string
  ) {
    super();

    this.clickThroughURLTemplates = clickThroughURLTemplates || [];
    this.clickTrackingURLTemplates = clickTrackingURLTemplates || [];
    this.impressionURLTemplates = impressionURLTemplates || [];
    this.errorURLTemplates = errorURLTemplates || [];
    this.viewableImpressionURLTemplates = viewableImpressionURLTemplates || [];
    this.trackingEvents = { ...trackingEvents };
    this.mediaFiles = mediaFiles;
    this.skipDelay = skipDelay;
    this.muted = muted;
    this.macros = macros;
    this.vastIcon = vastIcon;
    this.apiFramework = apiFramework;

    this.impressed = false;
    this.creativeViewed = false;
    this.skipable = false;
    this.progress = 0;
    this.duration = 0;
    this.quartiles = {};

    if (
      this.vastIcon &&
      this.vastIcon.tracking &&
      this.vastIcon.tracking.view
    ) {
      this.impressionURLTemplates.push(this.vastIcon.tracking.view);
    }

    // Tracker listeners should be notified with some events
    // no matter if there is a tracking URL or not
    this.emitAlwaysEvents = [
      'creativeView',
      'start',
      'firstQuartile',
      'midpoint',
      'thirdQuartile',
      'complete',
      'resume',
      'pause',
      'rewind',
      'skip',
      'closeLinear',
      'close',
      'mute',
      'unmute'
    ];

    this.initDuration(duration);
  }

  initDuration(duration: number): void {
    this.duration = duration;
    // beware of key names, theses are also used as event names
    this.quartiles = {
      firstQuartile: Math.round(25 * this.duration) / 100,
      midpoint: Math.round(50 * this.duration) / 100,
      thirdQuartile: Math.round(75 * this.duration) / 100
    };
  }

  setProgress(progress: number): void {
    if (this.skipDelay !== -1 && !this.skipable) {
      if (this.skipDelay > progress) {
        this.emit('skip-countdown', this.skipDelay - progress);
      } else {
        this.skipable = true;
        this.emit('skip-countdown', 0);
      }
    }
    if (this.duration > 0) {
      const events: string[] = [];

      if (progress > 0) {
        events.push('start');

        const percent = Math.round((progress / this.duration) * 100);
        events.push(`progress-${percent}%`);
        events.push(`progress-${Math.round(progress)}`);

        Object.keys(this.quartiles).forEach(quartile => {
          const time = this.quartiles[quartile];
          if (time <= progress) {
            events.push(quartile);
          }
        });
      }

      events.forEach(eventName => {
        this.track(eventName, true);
      });

      if (progress < this.progress) {
        this.track('rewind');
      }
    }

    this.progress = progress;
  }

  setMuted(muted: boolean): void {
    if (this.muted !== muted) {
      this.track(muted ? 'mute' : 'unmute');
    }
    this.muted = muted;
  }

  creativeView(): void {
    if (!this.creativeViewed) {
      this.creativeViewed = true;
      this.track('creativeView');
    }
  }

  impression(): void {
    if (!this.impressed) {
      this.impressed = true;
      this.trackURLs(this.impressionURLTemplates);
    }
  }

  setMRCViewable(): void {
    if (
      this.viewableImpressionURLTemplates &&
      this.viewableImpressionURLTemplates.length
    ) {
      this.trackURLs(this.viewableImpressionURLTemplates);
    }
  }

  // To be called when an error happen with the proper error code
  errorWithCode(errorCode: number): void {
    this.trackURLs(this.errorURLTemplates, { ERRORCODE: errorCode });
  }

  // To be called when the user watched the creative until it's end
  complete(): void {
    this.track('complete');
  }

  pause(): void {
    this.track('pause');
  }

  resume(): void {
    this.track('resume');
  }

  skip(): void {
    this.track('skip');
  }

  // To be called when the player or the window is closed during the ad
  close(): void {
    this.track('closeLinear');
  }

  // To be called when the user clicks on the creative
  click(url: string): string | null {
    if (
      this.clickTrackingURLTemplates &&
      this.clickTrackingURLTemplates.length
    ) {
      this.trackURLs(this.clickTrackingURLTemplates);
    }

    const clickThroughURLTemplates = [...this.clickThroughURLTemplates];

    // Used by VPAID
    if (url) {
      clickThroughURLTemplates.unshift(url);
    }

    if (clickThroughURLTemplates && clickThroughURLTemplates.length) {
      const variables = {
        CONTENTPLAYHEAD: progressFormatted(this.progress),
        ...this.macros
      };
      const clickThroughURL = resolveURLTemplates(
        clickThroughURLTemplates,
        variables
      )[0];

      return String(clickThroughURL).trim();
    } else {
      return null;
    }
  }

  getClickEndScreenUrl(endScreen: EndScreen | null): string | null {
    if (!endScreen) return null;

    if (endScreen.clickTracking) {
      this.trackURLs(endScreen.clickTracking);
    }

    if (endScreen.clickThrough) {
      const variables = {
        CONTENTPLAYHEAD: progressFormatted(this.progress),
        ...this.macros
      };
      const clickThroughURL = resolveURLTemplates(
        endScreen.clickThrough,
        variables
      )[0];

      return String(clickThroughURL).trim();
    } else {
      return null;
    }
  }

  track(eventName: string, once?: boolean): void {
    // closeLinear event was introduced in VAST 3.0
    // Fallback to vast 2.0 close event if necessary
    if (once === null || once === undefined) {
      once = false;
    }

    if (
      eventName === 'closeLinear' &&
      !this.trackingEvents[eventName] &&
      this.trackingEvents.close
    ) {
      eventName = 'close';
    }
    const trackingURLTemplates = this.trackingEvents[eventName];
    const idx = this.emitAlwaysEvents.indexOf(eventName);

    if (trackingURLTemplates) {
      this.emit(eventName, '');
      this.trackURLs(trackingURLTemplates);
    } else if (idx !== -1) {
      this.emit(eventName, '');
    }

    if (once === true) {
      delete this.trackingEvents[eventName];
      if (idx > -1) {
        this.emitAlwaysEvents.splice(idx, 1);
      }
    }
  }

  trackURLs(URLTemplates: string[], variables: Record<string, any> = {}): void {
    if (
      this.mediaFiles &&
      this.mediaFiles.length > 0 &&
      (this.mediaFiles[0] as any).fileURL
    ) {
      variables.ASSETURI = (this.mediaFiles[0] as any).fileURL;
    }
    variables.CONTENTPLAYHEAD = progressFormatted(this.progress);

    // Add custom macros to the tracking URLS
    variables = { ...variables, ...this.macros };

    track(URLTemplates, variables);
  }
}

export default VASTTracker;
